
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { Editor, EditorContent } from '@tiptap/vue-2'
import Bold from '@tiptap/extension-bold'
import Document from '@tiptap/extension-document'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import History from '@tiptap/extension-history'
import Link from '@tiptap/extension-link'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import TextAlign from '@tiptap/extension-text-align'
import ButtonEditorMenu from '@/components/molecules/ButtonEditorMenu.vue'

/**
 * HTMLエディタ(wysiwygエディタ)
 */
@Component({
  components: {
    EditorContent,
    ButtonEditorMenu,
  },
})
export default class HtmlEditor extends Vue {
  /** v-model対応用のvalue値 */
  @Prop({ default: '' })
  value!: string

  /** エディタ情報のインスタンス */
  public editorInstance: Editor | null = null

  /** 初期化完了フラグ */
  public isLoaded = false

  /**
   * エディタ情報取得
   *
   * @returns エディタ情報
   */
  private get editor(): Editor {
    return this.editorInstance as Editor
  }

  /**
   * エディタのフォーカス状況を取得
   *
   * @returns フォーカスされているか否か
   */
  private get isFocused(): boolean {
    return this.editor.isFocused
  }

  /**
   * リンク設定ボタンの有効状態を取得
   *
   * @returns 設定不可の場合はtrue、設定可能な場合はfalse
   */
  private get isLinkDisabled(): boolean {
    // リンク未設定かつ範囲未選択の場合、ボタンを無効化
    if (!this.editor.isActive('link') && this.editor.state.selection.empty) {
      return true
    }

    return false
  }

  /**
   * v-model対応用の更新監視
   *
   * @param value 入力値
   */
  @Watch('value')
  private updateValue(value: string): void {
    if (this.editor.getHTML() === value) {
      return
    }

    this.editor.commands.setContent(value, false)
  }

  /**
   * 初期処理
   */
  private mounted(): void {
    // インスタンスの生成
    this.editorInstance = new Editor({
      // 初期値指定
      content: this.value,
      // 拡張機能の読み込み
      extensions: [
        // 太字
        Bold,
        // ドキュメント(必須)
        Document,
        // 改行(brタグ)
        HardBreak,
        // タイトル
        Heading.configure({
          levels: [1, 2],
        }),
        // 履歴(Undo/Redo)
        History,
        // ハイパーリンク
        Link.configure({
          openOnClick: false,
        }),
        // 段落
        Paragraph,
        // プレーンテキスト(必須)
        Text,
        // テキスト位置指定
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['center'],
        }),
      ],
      // v-model対応用の更新時処理設定
      onUpdate: (): void => {
        this.$emit('input', this.editor.getHTML())
      },
      // エディタ部の属性指定
      editorProps: {
        attributes: {
          // クラス名指定(出力用のラッパーコンポーネントと揃える)
          class: 'html-content',
        },
      },
    })

    this.isLoaded = true
  }

  /**
   * メニューエリアのクリック時イベント
   */
  private clickMenuArea(): void {
    // エディタにフォーカスさせる
    this.editor.commands.focus()
  }

  /**
   * 中央揃えの切り替え
   *
   * @param isCenter 現在の中央揃え設定
   */
  private toggleAlignCenter(isCenter: boolean): void {
    if (isCenter) {
      this.editor.chain().focus().unsetTextAlign().run()
    } else {
      this.editor.chain().focus().setTextAlign('center').run()
    }
  }

  /**
   * ハイパーリンクの設定
   */
  private setLink(): void {
    // 設定済みのリンクURLを取得
    const currentUrl = this.editor.getAttributes('link').href

    // リンク先URLの入力受付
    const inputUrl = window.prompt('リンク先URLを入力してください\n(空白の場合はリンクを削除します)', currentUrl)

    // キャンセル処理
    if (inputUrl === null) {
      // フォーカスが外れてしまうため戻す
      this.editor.commands.focus()
      return
    }

    // ホワイトスペース除去
    const url = inputUrl.trim()

    // 空文字列の場合はリンク除去
    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    // 入力文字列のテスト
    if (!this.enableLinkUrl(url)) {
      alert('無効なURLです。')
      return
    }

    // リンク設定
    this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  /**
   * 文字列が有効なURLかを判定する
   *
   * @param url URL
   * @returns チェック結果
   */
  private enableLinkUrl(url: string): boolean {
    // 標準のURL.canParseによるチェック(絶対パスでない場合はfalseが返る)
    // 型定義の関係でエラーが発生するためlintを無効化している
    // eslint-disable-next-line
    return (URL as any).canParse(url)
  }

  /**
   * 破棄前処理
   */
  private beforeDestroy(): void {
    this.editor.destroy()
  }
}
